/*
 * WHERE SNIPPETS LIVE. The first version is DEMO-BACKED: snippets are kept in this browser's localStorage, so the owner can make one and open its link in the same browser to see exactly what a recipient will see.
 * Nothing leaves the browser. The real thing (a server collection behind the same four calls, the link readable without a login) replaces `shareApi` and nothing else changes: every screen talks to ShareApi.
 */
import { eventEmitter } from "../eventEmitters/EventEmitters";
import { ShareSnapshot, renewedExpiry, shareState } from "./shareSnapshot";
import { expandActions, Packed, packActions } from "./shareCompact";

/** a clip as kept: the visits packed (see shareCompact.ts), everything else as it is */
type Stored = Omit<ShareSnapshot, "actions"> & { packed: Packed };
const pack = (snapshot: ShareSnapshot): Stored => { const { actions, ...rest } = snapshot; return { ...rest, packed: packActions(actions) }; };
const unpack = (stored: Stored): ShareSnapshot => { const { packed, ...rest } = stored; return { ...rest, actions: expandActions(packed) }; };
/** how many bytes this clip takes to keep: what the dialog shows and what the store is held to */
export const storedSize = (snapshot: ShareSnapshot): number => JSON.stringify(pack(snapshot)).length;

/** what the plan allows, as the server counts it: links live now of the most it allows, and the longest a link may last */
export interface ShareLimits { maxLive: number; maxExpiryDays: number; live: number }

/** a link the server would not make, and why (the dialog says it in words) */
export class ShareRefused extends Error {
  constructor(readonly reason: string, readonly where?: string, readonly limits?: ShareLimits) { super(reason); }
}

export interface ShareApi {
  /** the biggest clip this store can keep, in bytes as stored (a browser's storage is a few megabytes in all; the server's limit is what it accepts) */
  maxBytes: number;
  /** makes the link and says what to give out: the snapshot's own token for a browser store, the one the server made (shown once) for a server */
  create: (snapshot: ShareSnapshot) => Promise<{ token: string; expiresAt?: number }>;
  /** a snippet by its token, whatever its state (the viewer says which); null when there is none */
  get: (token: string) => Promise<ShareSnapshot | null>;
  list: () => Promise<ShareSnapshot[]>;
  /** `key` is shareKey(link): a server link's id, or a browser link's token */
  revoke: (key: string) => Promise<void>;
  renew: (key: string, days: number) => Promise<void>;
  /** what the plan allows, or null where there is no plan to speak of (the browser's own store, the demo) */
  limits: () => Promise<ShareLimits | null>;
}

/** a clip as it travels to the server: packed, without what the server decides itself (the token, the expiry) or must never take from the browser */
export const clipForServer = (snapshot: ShareSnapshot): Omit<Stored, "token" | "expiresAt" | "revokedAt" | "draft" | "createdAt" | "id" | "views"> => {
  const { token, expiresAt, revokedAt, draft, createdAt, id, views, ...rest } = pack(snapshot) as any;

  return rest;
};

/** a clip as the server hands it back, ready to play */
export const snapshotFromServer = (clip: any, extra: { token: string; createdAt: number; expiresAt: number }): ShareSnapshot => unpack({ ...clip, ...extra } as Stored);

const KEY = "skydive.demoShares";
const MAX_KEPT = 25;

const DRAFT_KEPT_MS = 60 * 60 * 1000;

const readAll = (): Stored[] => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(KEY) || "[]");

    return Array.isArray(parsed) ? parsed.filter((s) => s && typeof s.token === "string" && !(s.draft && Date.now() - s.createdAt > DRAFT_KEPT_MS)) : [];
  } catch {
    return [];
  }
};

/** false when the browser would not keep it (full or blocked): the caller says so instead of handing out a link that will not open */
const writeAll = (all: Stored[]): boolean => {
  let kept = true;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(all.slice(0, MAX_KEPT)));
  } catch {
    kept = false;
  }
  eventEmitter.emit("sharesChanged");   // every list of links (the dialog's, Settings') draws itself again

  return kept;
};

/** a snippet read straight away (the viewer needs it before the dashboard's first request) */
export const peekSnapshot = (token: string): ShareSnapshot | null => { const found = readAll().find((s) => s.token === token); return found ? unpack(found) : null; };

export const demoShareApi: ShareApi = {
  maxBytes: 3_500_000,
  create: async (snapshot) => {
    if (!writeAll([pack(snapshot), ...readAll().filter((s) => s.token !== snapshot.token)])) throw new Error("this browser could not keep the clip");

    return { token: snapshot.token };
  },
  get: async (token) => peekSnapshot(token),
  // the list shows labels and dates, never the visits: they are not unpacked for it
  list: async () => readAll().filter((s) => !s.draft).sort((a, b) => b.createdAt - a.createdAt).map((s) => ({ ...(s as any), actions: [], packed: undefined }) as ShareSnapshot),
  revoke: async (token) => { writeAll(readAll().map((s) => (s.token === token && !s.revokedAt ? { ...s, revokedAt: Date.now() } : s))); },
  limits: async () => null,
  renew: async (token, days) => { writeAll(readAll().map((s) => (s.token === token && shareState(s, Date.now()) !== "revoked" ? { ...s, expiresAt: renewedExpiry(Date.now(), days) } : s))); },
};

/**
 * The drafts a preview needs ("Preview as the recipient") live in THIS browser only, in every build: a preview is for the owner's own eyes and must not become a public link, take one of the plan's links, or
 * reach the server.
 */
export const localShareApi: ShareApi = demoShareApi;

// the demo keeps links in the browser; every other build keeps them on the server (and the demo never makes a request: its fake server answers them all)
// eslint-disable-next-line @typescript-eslint/no-var-requires
export const shareApi: ShareApi = process.env.REACT_APP_DEMO === "1" ? demoShareApi : (require("./shareServerApi").serverShareApi as ShareApi);
